import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Returns the signed-in user's server-authoritative workspace roles. */
export const getSessionInfo = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId, claims } = context;
    const email = (claims as { email?: string })?.email?.toLowerCase() ?? "";
    const ownerEmails = (process.env["OWNER_EMAILS"] ?? "")
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);

    // OWNER_EMAILS is only an allowlist for provisioning. Workspace authority still
    // comes from the server-side user_roles table and never from client metadata.
    if (email && ownerEmails.includes(email)) {
      const { data: existing, error: ownerLookupError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "owner")
        .maybeSingle();

      if (ownerLookupError) {
        throw new Error(`Unable to verify owner access: ${ownerLookupError.message}`);
      }

      if (!existing) {
        const hasAdminConfig = Boolean(process.env["SUPABASE_URL"] && process.env["SUPABASE_SERVICE_ROLE_KEY"]);
        if (hasAdminConfig) {
          const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
          const { error: grantError } = await supabaseAdmin
            .from("user_roles")
            .insert({ user_id: userId, role: "owner" });
          if (grantError) throw new Error(`Unable to grant owner access: ${grantError.message}`);

          const { error: auditError } = await supabaseAdmin.from("audit_log").insert({
            actor_id: userId,
            action: "owner_role_granted",
            subject_type: "user",
            subject_id: userId,
            reason: "Email present in OWNER_EMAILS allowlist",
          });
          if (auditError) throw new Error(`Unable to audit owner access grant: ${auditError.message}`);
        } else {
          console.warn("[Shields] Owner upgrade skipped because the service-role secret is unavailable in this environment.");
        }
      }
    }

    const { data: roles, error: rolesError } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    if (rolesError) throw new Error(`Unable to verify workspace roles: ${rolesError.message}`);

    const roleList = (roles ?? []).map((role) => role.role as string);
    const { data: application, error: applicationError } = await supabase
      .from("partner_applications")
      .select("status")
      .eq("user_id", userId)
      .maybeSingle();
    if (applicationError) throw new Error(`Unable to verify Partner application: ${applicationError.message}`);

    return {
      userId,
      email,
      roles: roleList,
      isOwner: roleList.includes("owner"),
      isPartner: roleList.includes("partner"),
      partnerApplicationStatus: application?.status ?? null,
    };
  });
